import { z } from 'zod';
export const ROADMAP_DEFAULT_PAGE_SIZE = 100;
export var RoadmapItemStatus;
(function (RoadmapItemStatus) {
    RoadmapItemStatus["BACKLOG"] = "Backlog";
    RoadmapItemStatus["BUILDING"] = "Building";
    RoadmapItemStatus["SHIPPED"] = "Shipped";
    RoadmapItemStatus["CANCELED"] = "Canceled";
})(RoadmapItemStatus || (RoadmapItemStatus = {}));
export var RoadmapItemPriority;
(function (RoadmapItemPriority) {
    RoadmapItemPriority["URGENT"] = "Urgent";
    RoadmapItemPriority["HIGH"] = "High";
    RoadmapItemPriority["MEDIUM"] = "Medium";
    RoadmapItemPriority["LOW"] = "Low";
    RoadmapItemPriority["NO_PRIORITY"] = "No priority";
})(RoadmapItemPriority || (RoadmapItemPriority = {}));
const slackThreadUrlsSchema = z
    .array(z.url().refine((value) => {
    if (!URL.canParse(value))
        return false;
    const url = new URL(value);
    return (url.protocol === 'https:' &&
        /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.slack\.com$/.test(url.hostname) &&
        url.port === '' &&
        url.username === '' &&
        url.password === '' &&
        /^\/archives\/[CG][A-Z0-9]+\/p\d{16}\/?$/.test(url.pathname));
}, 'Expected a Slack message permalink'))
    .default([]);
const githubIssueUrlSchema = z
    .string()
    .regex(/^https:\/\/github\.com\/lightdash\/lightdash\/issues\/\d+\/?$/, 'Expected a public lightdash/lightdash issue URL');
const githubPullRequestUrlSchema = z
    .string()
    .regex(/^https:\/\/github\.com\/lightdash\/lightdash\/pull\/\d+\/?$/, 'Expected a public lightdash/lightdash pull request URL');
export const RoadmapItemSchema = z.object({
    ticketId: z.string().min(1).max(255),
    title: z.string().min(1),
    description: z.string().nullable(),
    status: z.enum(RoadmapItemStatus),
    priority: z.enum(RoadmapItemPriority),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    issueUrl: githubIssueUrlSchema.nullable(),
    pullRequestUrl: githubPullRequestUrlSchema.nullable(),
    slackThreadUrls: slackThreadUrlsSchema,
});
export const RoadmapProjectQuerySchema = z
    .object({
    page: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER).optional(),
    pageSize: z.number().int().min(1).max(100).optional(),
    statuses: z
        .string()
        .max(255)
        .refine((value) => value === '' ||
        value
            .split(',')
            .every((status) => [
            'backlog',
            'planned',
            'started',
            'paused',
            'completed',
            'canceled',
        ].includes(status)), 'Unknown roadmap status')
        .optional(),
    priorities: z
        .string()
        .max(255)
        .refine((value) => value === '' ||
        value
            .split(',')
            .every((priority) => Object.values(RoadmapItemPriority).includes(priority)), 'Unknown roadmap priority')
        .optional(),
    search: z.string().max(255).optional(),
    onlyInterested: z.boolean().optional(),
})
    .strict();
export const RoadmapQuerySchema = RoadmapProjectQuerySchema.omit({
    onlyInterested: true,
}).extend({
    projectId: z.string().min(1).max(255).optional(),
});
export const RoadmapPaginationSchema = z.object({
    page: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER),
    pageSize: z.number().int().min(1).max(100),
    totalIssues: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    totalPages: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
});
const roadmapCountSchema = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
const RoadmapStatusCountsSchema = z.object({
    [RoadmapItemStatus.BACKLOG]: roadmapCountSchema,
    [RoadmapItemStatus.BUILDING]: roadmapCountSchema,
    [RoadmapItemStatus.SHIPPED]: roadmapCountSchema,
    [RoadmapItemStatus.CANCELED]: roadmapCountSchema,
});
export const RoadmapFacetsSchema = z.object({
    statusCounts: RoadmapStatusCountsSchema,
    priorityCounts: z.object({
        [RoadmapItemPriority.URGENT]: roadmapCountSchema,
        [RoadmapItemPriority.HIGH]: roadmapCountSchema,
        [RoadmapItemPriority.MEDIUM]: roadmapCountSchema,
        [RoadmapItemPriority.LOW]: roadmapCountSchema,
        [RoadmapItemPriority.NO_PRIORITY]: roadmapCountSchema,
    }),
});
const RoadmapRequestSchema = RoadmapItemSchema.extend({
    projectId: z.string().min(1).nullable().optional(),
});
export const RoadmapResponseSchema = z.object({
    status: z.literal('ok'),
    results: z.array(RoadmapRequestSchema),
    expiresAt: z.string().datetime({ offset: true }).optional(),
    pagination: RoadmapPaginationSchema,
    facets: RoadmapFacetsSchema,
});
export const RoadmapProjectRequestsQuerySchema = RoadmapQuerySchema.required({
    projectId: true,
});
const RoadmapProjectPaginationSchema = z.object({
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1).max(100),
    totalResults: z.number().int().min(0),
    totalPages: z.number().int().min(0),
});
export const RoadmapProjectResultsSchema = z.object({
    projects: z.array(z.object({
        project: z.object({
            projectId: z.string().min(1),
            title: z.string().trim().min(1),
            description: z.string(),
            stage: z.enum([
                'backlog',
                'planned',
                'started',
                'paused',
                'completed',
            ]),
            progress: z.number().min(0).max(100),
            priority: z.enum(RoadmapItemPriority),
            issueStatusCounts: RoadmapStatusCountsSchema,
            lastIssueUpdatedAt: z
                .string()
                .datetime({ offset: true })
                .nullable(),
        }),
        ownRequestCount: z.number().int().min(0),
        hasDirectNeed: z.boolean(),
        slackThreadUrls: slackThreadUrlsSchema,
    })),
    otherRequestCount: z.number().int().min(0),
    pagination: RoadmapProjectPaginationSchema,
    expiresAt: z.string().datetime({ offset: true }),
});
export const RoadmapProjectRequestsResponseSchema = RoadmapResponseSchema.extend({
    results: z.array(RoadmapRequestSchema.required({ projectId: true })),
    expiresAt: z.string().datetime({ offset: true }),
});
export const RoadmapProjectRequestsResultsSchema = RoadmapProjectRequestsResponseSchema.omit({
    status: true,
    results: true,
}).extend({
    data: RoadmapProjectRequestsResponseSchema.shape.results,
});
export const RoadmapProjectResponseSchema = z.object({
    status: z.literal('ok'),
    results: RoadmapProjectResultsSchema,
});
export const RoadmapFollowProjectRequestSchema = z
    .object({
    note: z
        .string()
        .trim()
        .min(1, 'Tell us why you are interested in this feature.')
        .max(2000),
})
    .strict();
export const RoadmapFollowProjectResultsSchema = z.object({
    message: z.string().min(1),
});
export const RoadmapFollowProjectResponseSchema = z.object({
    status: z.literal('ok'),
    results: RoadmapFollowProjectResultsSchema,
});
//# sourceMappingURL=roadmap.js.map